/**
 * Utility functions for cleaning track metadata (titles and artists)
 * Removes remix info, featured artists, remaster tags, and other common additions
 */

/**
 * Patterns to remove from track titles
 * Order matters - more specific patterns should come first
 */
const TITLE_CLEANUP_PATTERNS: RegExp[] = [
  // Remaster/reissue info
  /\s*[-–—]\s*\d{4}\s*(Remaster(ed)?|Reissue|Re-?master)/gi,
  /\s*\(\s*\d{4}\s*(Remaster(ed)?|Reissue|Re-?master)\s*\)/gi,
  /\s*\[\s*\d{4}\s*(Remaster(ed)?|Reissue|Re-?master)\s*\]/gi,
  /\s*[-–—]\s*(Remaster(ed)?|Reissue|Re-?master)\s*\d{4}/gi,
  /\s*\(\s*(Remaster(ed)?|Reissue|Re-?master)\s*\d{4}\s*\)/gi,
  /\s*\[\s*(Remaster(ed)?|Reissue|Re-?master)\s*\d{4}\s*\]/gi,
  /\s*[-–—]\s*(Remaster(ed)?|Reissue|Re-?master(ed)?(\s+Version)?)/gi,
  /\s*\(\s*(Remaster(ed)?|Reissue|Re-?master(ed)?(\s+Version)?)\s*\)/gi,
  /\s*\[\s*(Remaster(ed)?|Reissue|Re-?master(ed)?(\s+Version)?)\s*\]/gi,

  // Remix patterns - remove remix info in parentheses/brackets
  /\s*\(\s*[^)]*\s+(Remix|Rmx|Mix|Edit|Dub|VIP|Bootleg|Rework|Flip)\s*\)/gi,
  /\s*\[\s*[^\]]*\s+(Remix|Rmx|Mix|Edit|Dub|VIP|Bootleg|Rework|Flip)\s*\]/gi,
  /\s*[-–—]\s*[^-–—]*\s+(Remix|Rmx|Mix|Edit|Dub|VIP|Bootleg|Rework|Flip)\s*$/gi,

  // Specific mix types
  /\s*\(\s*(Radio|Club|Extended|Original|Instrumental|Acoustic|Live|Dub|VIP|Short|Long)\s*(Edit|Mix|Version|Cut)?\s*\)/gi,
  /\s*\[\s*(Radio|Club|Extended|Original|Instrumental|Acoustic|Live|Dub|VIP|Short|Long)\s*(Edit|Mix|Version|Cut)?\s*\]/gi,
  /\s*[-–—]\s*(Radio|Club|Extended|Original|Instrumental|Acoustic|Live|Dub|VIP|Short|Long)\s*(Edit|Mix|Version|Cut)?\s*$/gi,

  // Explicit/clean tags
  /\s*\(\s*(Explicit|Clean|Dirty|Radio|Album)\s*(Version)?\s*\)/gi,
  /\s*\[\s*(Explicit|Clean|Dirty|Radio|Album)\s*(Version)?\s*\]/gi,

  // Single/bonus track markers
  /\s*\(\s*(Single|Bonus(\s+Track)?|Deluxe|Premium)\s*(Version|Edition)?\s*\)/gi,
  /\s*\[\s*(Single|Bonus(\s+Track)?|Deluxe|Premium)\s*(Version|Edition)?\s*\]/gi,
  /\s*[-–—]\s*(Single|Bonus(\s+Track)?|Deluxe|Premium)\s*(Version|Edition)?\s*$/gi,

  // Featured artists in titles
  /\s*\(\s*(feat\.?|ft\.?|featuring|with|prod\.?\s+by|produced\s+by)\s+[^)]+\)/gi,
  /\s*\[\s*(feat\.?|ft\.?|featuring|with|prod\.?\s+by|produced\s+by)\s+[^\]]+\]/gi,
  /\s+[-–—]\s*(feat\.?|ft\.?|featuring)\s+.+$/gi,

  // Year in parentheses (often indicates version year)
  /\s*\(\s*\d{4}\s*\)/g,
  /\s*\[\s*\d{4}\s*\]/g,

  // From album/soundtrack info
  /\s*\(\s*(From|From\s+the)[^)]+\)/gi,
  /\s*\[\s*(From|From\s+the)[^\]]+\]/gi,

  // Official/unofficial markers
  /\s*\(\s*(Official|Unofficial)?\s*(Audio|Video|Lyric\s*Video?)\s*\)/gi,
  /\s*\[\s*(Official|Unofficial)?\s*(Audio|Video|Lyric\s*Video?)\s*\]/gi,

  // Free download / streaming markers
  /\s*\(\s*(Free\s+Download|Free|Stream)\s*\)/gi,
  /\s*\[\s*(Free\s+Download|Free|Stream)\s*\]/gi,
];

/**
 * Patterns to remove from artist names
 */
const ARTIST_CLEANUP_PATTERNS: RegExp[] = [
  // Featured artists
  /\s*(feat\.?|ft\.?|featuring|with)\s+.+$/gi,
  /\s*\(\s*(feat\.?|ft\.?|featuring|with)\s+[^)]+\)/gi,
  /\s*\[\s*(feat\.?|ft\.?|featuring|with)\s+[^\]]+\]/gi,

  // Producer credits in artist field
  /\s*\(\s*prod\.?\s+by\s+[^)]+\)/gi,
  /\s*\[\s*prod\.?\s+by\s+[^\]]+\]/gi,
  /\s*[-–—]\s*prod\.?\s+by\s+.+$/gi,

  // X/&/vs patterns for collaborations - only the additions
  /\s+x\s+.+$/gi,
  /\s+vs\.?\s+.+$/gi,

  // Presents/pres patterns
  /\s+(presents?|pres\.)\s+.+$/gi,
];

/**
 * Clean a track title by removing common additions
 */
export function cleanTitle(title: string): string {
  if (!title || typeof title !== 'string') return title;

  let cleaned = title
    .trim()
    .replace(/^["'](.*)["']$/, '$1')
    .trim();

  // Apply each pattern in order
  for (const pattern of TITLE_CLEANUP_PATTERNS) {
    cleaned = cleaned.replace(pattern, '');
  }

  // Clean up any leftover artifacts
  cleaned = cleaned
    .replace(/\s*[-–—]\s*$/, '') // Trailing dashes
    .replace(/\s{2,}/g, ' ') // Multiple spaces
    .replace(/^\s+|\s+$/g, ''); // Trim

  return cleaned || title; // Return original if cleaning removed everything
}

/**
 * Clean an artist name by removing featured artists and other additions
 */
export function cleanArtist(artist: string): string {
  if (!artist || typeof artist !== 'string') return artist;

  let cleaned = artist
    .trim()
    .replace(/^["'](.*)["']$/, '$1')
    .trim();

  // Apply each pattern in order
  for (const pattern of ARTIST_CLEANUP_PATTERNS) {
    cleaned = cleaned.replace(pattern, '');
  }

  // Clean up any leftover artifacts
  cleaned = cleaned
    .replace(/\s*[-–—]\s*$/, '') // Trailing dashes
    .replace(/\s*[,&]\s*$/, '') // Trailing commas/ampersands
    .replace(/\s{2,}/g, ' ') // Multiple spaces
    .replace(/^\s+|\s+$/g, ''); // Trim

  return cleaned || artist; // Return original if cleaning removed everything
}

/**
 * Clean both title and artist for a track
 */
export function cleanTrackMetadata(track: { Title: string; Artist: string }): {
  title: string;
  artist: string;
  wasModified: boolean;
} {
  const cleanedTitle = cleanTitle(track.Title);
  const cleanedArtist = cleanArtist(track.Artist);

  return {
    title: cleanedTitle,
    artist: cleanedArtist,
    wasModified: cleanedTitle !== track.Title || cleanedArtist !== track.Artist,
  };
}
